import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  MdLocalOffer,
  MdHeadsetMic,
  MdPeople,
  MdLogout,
  MdStickyNote2,
  MdMenu,
  MdClose,
  MdDescription,
  MdFolderOpen
} from "react-icons/md";
import { FaBox } from "react-icons/fa";
import { getRole } from "../api/users";
import { logout } from "../api/auth";
import rpmLogo from "../assets/RPM-logo.png";
import toast from "react-hot-toast";

export default function TopBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState("");
  const [role, setRole] = useState<string | null>(null); // null = not logged in
  const [menuOpen, setMenuOpen] = useState(false);

  // Fetch role only if logged in
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      setRole(null);
      return;
    }

    const fetchRole = async () => {
      try {
        const data = await getRole();
        setRole(data.role);
      } catch (err) {
        console.error("Failed to fetch role", err);
        setRole(null);
      }
    };
    fetchRole();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      toast.error("Could not log out");
    }
  };

  const baseItems: any[] = [{ label: "Specs", icon: <MdDescription size={22} />, path: "/specs" }];

  if (role) {
    baseItems.unshift({ label: "Offers", icon: <MdLocalOffer size={24} />, path: "/offers" });

    // Admins
    if (role === "superadmin") {
      baseItems.push(
        { label: "Users", icon: <MdPeople size={22} />, path: "/admin/users" },
        { label: "Manage Offers", icon: <MdStickyNote2 size={22} />, path: "/admin/manageoffers" },
        { label: "Manage Supply", icon: <FaBox size={22} />, path: "/admin/supply" },
        { label: "Manage Specs", icon: <MdFolderOpen size={22} />, path: "/admin/specs" },
      );
    }
    // Normal user
    else if (role === "user") {
      baseItems.push({
        label: "Support",
        icon: <MdHeadsetMic size={22} />,
        path: "/support",
      });
    }

    // Logout for everyone logged in
    baseItems.push({
      label: "Logout",
      icon: <MdLogout size={22} />,
      path: "/logout",
      action: () => handleLogout(),
    });
  }

  // Track active menu
  useEffect(() => {
    const current = baseItems.find((item) => location.pathname.startsWith(item.path));
    if (current) setActive(current.label);
  }, [location.pathname, role]);

  const handleClick = (item: any) => {
    setActive(item.label);
    setMenuOpen(false);
    if (item.action) item.action();
    else navigate(item.path);
  };

  return (
    <header className="w-full bg-[#606060] h-20 flex items-center px-6 shadow relative z-50">
      {/* Logo + title */}
      <div className="flex items-center mr-auto">
        <img
          src={rpmLogo}
          alt="RPM Logo"
          className="mr-3"
          style={{ height: "60px", width: "auto", objectFit: "contain" }}
        />
        <span className="text-2xl text-white md:text-4xl font-condensed tracking-wide mt-2">
          Marketplace
        </span>
      </div>

      {/* Desktop nav */}
      <nav className="hidden md:flex gap-2">
        {baseItems.map((item) => (
          <div
            key={item.label}
            className={`flex flex-col items-center justify-center px-4 py-1.5 rounded-lg font-medium text-sm cursor-pointer transition
              ${active === item.label
                ? "bg-[#F97316] text-white"
                : "text-white hover:bg-[#F97316] hover:text-white"
              }`}
            onClick={() => handleClick(item)}
          >
            <span>{item.icon}</span>
            <span className="text-[11px] mt-0.5">{item.label}</span>
          </div>
        ))}
      </nav>

      {/* Mobile menu button */}
      <button
        className="md:hidden text-white ml-4"
        onClick={() => setMenuOpen(!menuOpen)}
      >
        {menuOpen ? <MdClose size={26} /> : <MdMenu size={26} />}
      </button>

      {/* Mobile dropdown */}
      {menuOpen && (
        <div className="absolute top-20 left-0 w-full bg-[#606060] flex flex-col items-start py-3 shadow-lg md:hidden animate-fadeIn">
          {baseItems.map((item) => (
            <div
              key={item.label}
              className={`w-full flex items-center gap-3 px-6 py-3 text-sm cursor-pointer transition
                ${active === item.label
                  ? "bg-[#F97316] text-white"
                  : "text-white hover:bg-[#F97316] hover:text-white"
                }`}
              onClick={() => handleClick(item)}
            >
              {item.icon}
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      )}
    </header>
  );
}